// Timestamp-based countdown.
// We do NOT rely on setInterval to keep accurate time — instead we store
// the absolute end time and recompute remaining seconds on every tick AND
// on every visibilitychange. This way background throttling (iOS Safari
// kills/slows timers when the tab is hidden) doesn't corrupt the display
// or skip the finish event.
import { playBell } from './sound.js';

export type TimerHandle = {
  stop: () => void;
};

export function startRestTimer(
  seconds: number,
  target: HTMLElement,
  onFinish?: (overdueSeconds: number) => void,
): TimerHandle {
  const endAt = Date.now() + seconds * 1000;
  let stopped = false;
  let finished = false;
  let intervalId: ReturnType<typeof setInterval> | null = null;

  function tick() {
    if (stopped) return;
    const remainingMs = endAt - Date.now();
    const remaining = Math.max(0, Math.round(remainingMs / 1000));
    paint(remaining);
    if (remaining <= 0 && !finished) {
      finished = true;
      if (intervalId !== null) clearInterval(intervalId);
      const overdueSeconds = Math.max(0, Math.round(-remainingMs / 1000));
      void buzz();
      onFinish?.(overdueSeconds);
    }
  }

  function paint(remaining: number) {
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    target.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    target.classList.toggle('is-zero', remaining <= 0);
  }

  function onVisible() {
    if (document.visibilityState === 'visible') tick();
  }

  document.addEventListener('visibilitychange', onVisible);
  intervalId = setInterval(tick, 250); // 4×/s while visible — cheap and snappy
  tick();

  return {
    stop: () => {
      stopped = true;
      if (intervalId !== null) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    },
  };
}

async function buzz() {
  try {
    navigator.vibrate?.([180, 60, 180]);
  } catch {
    /* ignore */
  }
  playBell();
}
